import React, { useEffect, useMemo, useRef, useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { FaPlus, FaSearch, FaTimes } from "react-icons/fa";
import { DEMO_TAG_DEFINITIONS } from "../demo/demoContacts";

// ---------------------------------------------------------------------------
// Module-level cache for the tag definitions list. Loaded once per session
// (the system catalogue is tiny and stable). Each Contacts page row mounts
// a picker on-demand, so without a cache we'd refetch the full list every
// time the popover opens.
// ---------------------------------------------------------------------------
let DEFINITIONS_PROMISE = null;
function loadDefinitions() {
  // Demo mode: serve the sample system tag catalogue so the picker shows its
  // category tabs (intent · lifecycle · persona · admin) instead of an empty
  // list. Not cached, so the real catalogue is fetched once the demo ends.
  if (typeof window !== "undefined" && window.__demoMode) {
    return Promise.resolve(DEMO_TAG_DEFINITIONS);
  }
  if (DEFINITIONS_PROMISE) return DEFINITIONS_PROMISE;
  DEFINITIONS_PROMISE = handleTokenAndPostRequest(
    `${window.api_host}/contact-tags/list-definitions`,
    {}
  )
    .then((res) => (res?.data?.tags || []))
    .catch(() => {
      // null-out on failure so the next open retries instead of caching the error.
      DEFINITIONS_PROMISE = null;
      return [];
    });
  return DEFINITIONS_PROMISE;
}

export function clearTagDefinitionsCache() {
  DEFINITIONS_PROMISE = null;
}

// Hex → readable text colour. Treats anything darker than ~mid-grey as dark.
function pickTextColor(hex) {
  if (!hex || typeof hex !== "string" || hex[0] !== "#" || hex.length !== 7) {
    return "#1f2937";
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Relative luminance (sRGB → linear approximation).
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#1f2937" : "#fff";
}

/**
 * Small coloured chip for a single tag. Optionally renders an "x" button so
 * the agent can remove the tag inline (only shown when `onRemove` is set).
 */
export function ContactTagChip({ tag, onRemove }) {
  const bg = tag.color || "#e5e7eb";
  const fg = pickTextColor(bg);
  return (
    <span
      title={tag.label}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 999,
        background: bg, color: fg,
        fontSize: 11, fontWeight: 600,
        lineHeight: 1.4,
        maxWidth: 180,
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {tag.label}
      </span>
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
          title="Remove tag"
          style={{
            background: "transparent", border: "none",
            color: fg, opacity: 0.7, cursor: "pointer",
            padding: 0, marginLeft: 2, display: "inline-flex",
          }}
        >
          <FaTimes style={{ fontSize: 9 }} />
        </button>
      )}
    </span>
  );
}

/**
 * Popover that lists every tag definition the agency can see, grouped by
 * category. Clicking a tag invokes `onPick(tagDefinition)` — the parent owns
 * the actual add/remove network call so it can stay optimistic.
 *
 * Props:
 *   anchorEl       — DOM element to anchor the popover under
 *   onClose        — close handler
 *   onPick         — (tagDef) => void; fires when a tag is clicked
 *   appliedSlugs   — Set<string> of slugs already on the contact (rendered as
 *                    "applied" so clicking them removes instead of adding)
 *   onRemove       — optional (tagDef) => void; called when an applied tag is
 *                    clicked. Falls back to onPick.
 */
export default function ContactTagPicker({
  anchorEl,
  onClose,
  onPick,
  appliedSlugs,
  onRemove,
  // Optional override so callers rendered inside a high-z-index modal
  // (e.g. AIConversationModal at zIndex 10000) can lift the popover above
  // the modal overlay. Defaults to the original value.
  zIndex = 1300,
}) {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const popoverRef = useRef(null);
  // Inline "create custom tag" form state. Toggled from the footer button.
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState("persona");
  const [newColor, setNewColor] = useState("#6b7280");
  const [createError, setCreateError] = useState("");
  const [creatingBusy, setCreatingBusy] = useState(false);

  // Slugify the label the same way the backend does so the agent sees the
  // exact id their tag will get (and "already exists" feedback fires early).
  const slugFromLabel = (s) => (s || "").toLowerCase().trim()
    .replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");

  const submitCreate = async () => {
    const label = newLabel.trim();
    const slug = slugFromLabel(label);
    if (!label || !slug) { setCreateError("Label is required"); return; }
    if (!/^#[0-9a-fA-F]{6}$/.test(newColor)) { setCreateError("Pick a hex colour"); return; }
    if (defs.some((d) => d.slug === slug)) { setCreateError("A tag with that name already exists"); return; }
    // Demo mode: create the tag locally so the picker stays interactive
    // without persisting anything to the backend.
    if (typeof window !== "undefined" && window.__demoMode) {
      const tag = { id: `demo-${slug}`, slug, label, color: newColor, category: newCategory, source: "human", is_system: false };
      setDefs((prev) => [...prev, tag]);
      setCreating(false);
      setNewLabel("");
      setSearch("");
      setCreateError("");
      onPick?.(tag);
      return;
    }
    setCreatingBusy(true);
    setCreateError("");
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/contact-tags/create-custom`,
        { slug, label, color: newColor, category: newCategory },
      );
      const tag = res?.data?.tag;
      if (!tag) throw new Error("No tag returned");
      // Drop the cache so the next picker mount sees the new tag, and add it
      // to the open list immediately so the agent can click to apply it.
      clearTagDefinitionsCache();
      setDefs((prev) => [...prev, tag]);
      setCreating(false);
      setNewLabel("");
      setSearch("");
      onPick?.(tag);
    } catch (e) {
      setCreateError(e?.response?.data?.message || "Failed to create tag");
    } finally {
      setCreatingBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadDefinitions().then((d) => {
      if (cancelled) return;
      setDefs(Array.isArray(d) ? d : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // Position the popover directly under the anchor. Recompute on scroll/resize.
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!anchorEl) return;
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      setPos({ top: r.bottom + 2, left: Math.min(r.left, window.innerWidth - 320) });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorEl]);

  // Click-outside to dismiss.
  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [anchorEl, onClose]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = defs.filter((d) => {
      if (!q) return true;
      return (
        (d.label || "").toLowerCase().includes(q) ||
        (d.slug || "").toLowerCase().includes(q) ||
        (d.description || "").toLowerCase().includes(q)
      );
    });
    const map = new Map();
    for (const d of filtered) {
      const cat = d.category || "other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(d);
    }
    // Stable ordering of categories matching the seed in schema_changelog.sql.
    const order = ["intent", "lifecycle", "persona", "admin", "other"];
    return order
      .filter((c) => map.has(c))
      .map((c) => [c, map.get(c)])
      .concat(
        [...map.keys()].filter((c) => !order.includes(c)).map((c) => [c, map.get(c)])
      );
  }, [defs, search]);

  if (!anchorEl) return null;
  const applied = appliedSlugs instanceof Set ? appliedSlugs : new Set(appliedSlugs || []);

  return (
    <div
      ref={popoverRef}
      style={{
        position: "fixed", top: pos.top, left: pos.left, zIndex,
        width: 320, maxHeight: 380, overflowY: "auto",
        background: "#fff", borderRadius: 10,
        border: "1px solid #e5e7eb",
        boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
        padding: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <FaSearch style={{ fontSize: 11, color: "#9ca3af" }} />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Find a tag..."
          style={{
            flex: 1, border: "none", outline: "none",
            fontSize: 13, padding: "2px 4px",
          }}
        />
      </div>
      {loading ? (
        <div style={{ padding: 16, fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
          Loading tags…
        </div>
      ) : grouped.length === 0 ? (
        <div style={{ padding: 16, fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
          No matching tags
        </div>
      ) : (
        grouped.map(([cat, items]) => (
          <div key={cat} style={{ marginBottom: 8 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#9ca3af",
              textTransform: "uppercase", letterSpacing: 0.5,
              padding: "4px 4px",
            }}>
              {cat}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 2 }}>
              {items.map((d) => {
                const isApplied = applied.has(d.slug);
                const bg = d.color || "#e5e7eb";
                const fg = pickTextColor(bg);
                return (
                  <button
                    key={d.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isApplied && onRemove) onRemove(d);
                      else onPick?.(d);
                    }}
                    title={d.description || d.label}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 8px", borderRadius: 999,
                      background: isApplied ? bg : "transparent",
                      color: isApplied ? fg : "#374151",
                      border: `1px solid ${isApplied ? bg : "#e5e7eb"}`,
                      fontSize: 11, fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {isApplied ? <FaTimes style={{ fontSize: 8, opacity: 0.8 }} /> : <FaPlus style={{ fontSize: 8 }} />}
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
      <div style={{
        borderTop: "1px solid #f3f4f6", marginTop: 6, paddingTop: 6,
      }}>
        {creating ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "2px 2px 4px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>New custom tag</div>
            <input
              autoFocus
              value={newLabel}
              onChange={(e) => { setNewLabel(e.target.value); setCreateError(""); }}
              placeholder="Tag label (e.g. Beach houses)"
              style={{
                border: "1px solid #e5e7eb", borderRadius: 6,
                padding: "4px 6px", fontSize: 12, outline: "none",
              }}
            />
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{
                  flex: 1, fontSize: 11, padding: "3px 4px",
                  border: "1px solid #e5e7eb", borderRadius: 6, background: "#fff",
                }}
              >
                <option value="intent">intent</option>
                <option value="lifecycle">lifecycle</option>
                <option value="persona">persona</option>
                <option value="admin">admin</option>
                <option value="other">other</option>
              </select>
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                title="Chip colour"
                style={{
                  width: 30, height: 26, padding: 0, border: "1px solid #e5e7eb",
                  borderRadius: 6, background: "#fff", cursor: "pointer",
                }}
              />
            </div>
            {newLabel && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "#6b7280" }}>
                Preview:
                <ContactTagChip tag={{ label: newLabel, color: newColor }} />
              </div>
            )}
            {createError && (
              <div style={{ fontSize: 11, color: "#dc2626" }}>{createError}</div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button
                onClick={(e) => { e.stopPropagation(); setCreating(false); setCreateError(""); }}
                style={{
                  background: "transparent", border: "1px solid #e5e7eb",
                  borderRadius: 6, padding: "3px 8px", fontSize: 11,
                  cursor: "pointer", color: "#374151",
                }}
              >
                Cancel
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); submitCreate(); }}
                disabled={creatingBusy || !newLabel.trim()}
                style={{
                  background: creatingBusy ? "#9ca3af" : "#2563eb",
                  color: "#fff", border: "none",
                  borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                  cursor: creatingBusy ? "wait" : "pointer",
                }}
              >
                {creatingBusy ? "Creating…" : "Create & apply"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCreating(true);
                if (search.trim()) setNewLabel(search.trim());
              }}
              style={{
                background: "transparent", border: "1px dashed #d1d5db",
                color: "#374151", fontSize: 11, fontWeight: 600,
                borderRadius: 6, padding: "3px 8px", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <FaPlus style={{ fontSize: 9 }} /> New tag
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
